import React, { useEffect, useMemo, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { Form, Tab } from 'semantic-ui-react'
import { useAuth } from '~/routes/components/AuthProvider'
import { StyledMenuItem, StyledTabPane } from '../style'
import LanguageTab from '../LanguageTab'
import { FormFields } from '~/components/FormFields'
import { Nullable } from '~/types'
import { PopupOptionsType } from '~/api/services/loginPopups'
import { getDateWithTime, getMaxDate, getMinDate } from '~/utils/helpers'
import dayjs from 'dayjs'
import { getError } from '~/components/FormFields/util'
import { DateTimeInput } from 'semantic-ui-calendar-react'
import { StyledErrorLabel } from '~/pages/Dairy/MilkMoney/MilkDelivery/ShowTours/style'
import DataFieldsTable, { FIELD_ACTION } from './DataFieldsTable'

interface DataPopupsProps {
  optionsData: Nullable<PopupOptionsType>
}

const DataPopup: React.FC<DataPopupsProps> = ({ optionsData }) => {
  const { control,
    watch,
    setValue,
    getValues,
    formState } = useFormContext()

  const { id } = useParams()
  const { systemData: { languages, logged: { languageId } } } = useAuth()
  const languageLocal = languages.find(language => language.id === languageId)

  const { t } = useTranslation()
  const [activeIndex, setActiveIndex] = useState(0)

  const isSubmitted = formState.isSubmitted
  const watchDateFrom = watch('from')
  const watchDateTo = watch('to')
  const watchRoles = watch('roles')

  useEffect(() => {
    if (id || !optionsData?.fields || !watchRoles) return
    const role = optionsData.roles.find(r => r.id === watchRoles)
    if (!role) return
    const optionFields = optionsData.fields[role.slug] || []
    const currentFields = getValues('fields')
    if (!currentFields?.length) return
    const enriched = currentFields.map(formField => {
      const optionField = optionFields.find(f => f.slug === formField.slug)
      return optionField ? { ...optionField, ...formField } : formField
    })
    setValue('fields', enriched)
  }, [id, watchRoles, optionsData])

  function renderDataTables() {
    const role = optionsData?.roles.find(roleItem => roleItem.id === watchRoles)
    if (role) {
      return <DataFieldsTable
        roleSlug={role.slug}
        optionsData={optionsData}
      />
    }
  }

  function getMenuItem(pane, index) {
    const languagesErrors = formState.errors?.languages
    const paneHasError = index in (languagesErrors || {})

    return <StyledMenuItem
      key={pane}
      className='tab-item'
    >
      <div className={`${paneHasError ? 'error' : ''}`}>{pane}</div>
    </StyledMenuItem>
  }

  function renderLanguageFields() {
    const langArray = getValues('languages') || languages
    return <Tab
      menu={{ pointing: true }}
      renderActiveOnly={false}
      onTabChange={(_, data) => setActiveIndex(data.activeIndex as number)}
      style={{ marginTop: '1.2em' }}
      panes={langArray.map((language, index) => {
        return {
          menuItem: getMenuItem(language.label, index),
          pane: <StyledTabPane key={language.label}>
            <LanguageTab
              index={index}
              activeIndex={activeIndex}
              languageId={language.id.toString()}
              removeUpload
            />
          </StyledTabPane>
        }
      })}
    />
  }

  return <>
    <Form.Group widths={'equal'}>
      <FormFields.Input
        control={control}
        name={'name'}
        placeholder={t('Name of login popup')}
        label={t('Login popup name')}
        rules={{
          required: t('Please enter Login popup name'),
          maxLength: {
            value: 100,
            message: t('Max. 100 characters allowed')
          }
        }}
      />

    </Form.Group>
    <Form.Group widths={'equal'}>
      <FormFields.Select
        label={t('Roles')}
        control={control}
        name={'roles'}
        rules={{
          required: t('Please select role')
        }}
        onChange={(_, data) => {
          const role = optionsData?.roles.find(roleItem => roleItem.id === data.value)
          setValue('roles', data.value)
          if (role?.slug) {
            setValue('fields', optionsData?.fields[role.slug].map(field => ({ ...field, action: field.required ? FIELD_ACTION.DISPLAY : FIELD_ACTION.NONE })))
          }
        }}
        placeholder={t('Select roles')}
        options={optionsData?.roles.map(role => ({ value: role.id, text: role.label })) || []}
        selectOnBlur={false}
        selection
      />
      {Boolean(watchRoles) && optionsData?.farmerGroups?.length > 0 && <FormFields.Select
        label={t('Farmer groups')}
        control={control}
        name={'farmerGroups'}
        search
        rules={{
          required: t('Please select farmer group')
        }}
        noResultsMessage={t('No results found....')}
        placeholder={t('Select farmer group')}
        options={optionsData?.farmerGroups?.map(farmerGroup => ({ value: farmerGroup.id, text: farmerGroup.name })) || []}
        multiple
        selectOnBlur={false}
        selection
      />}
    </Form.Group>
    {renderDataTables()}
    <Form.Group widths={'equal'}>
      <Controller
        control={control}
        name={'from'}
        rules={{
          required: t('Please enter Date from'),
          validate: () => {
            const dateFrom = watchDateFrom ? getDateWithTime(watchDateFrom) : ''
            const currentDate = getDateWithTime(dayjs(new Date()).format('DD.MM.YYYY HH:mm'))
            if (dateFrom && currentDate && dayjs(dateFrom).diff(dayjs(currentDate)) <= 0) {
              return t('Please select a date ahead of the current date.')
            }
            return true
          }
        }}
        render={({ fieldState }) => {
          const dateFrom = watchDateFrom ? getDateWithTime(watchDateFrom) : ''
          const currentDate = getDateWithTime(dayjs(new Date()).format('DD.MM.YYYY HH:mm'))
          return <Form.Field
            required
            error={!watchDateFrom && isSubmitted ? getError(fieldState) : dateFrom && currentDate && dayjs(dateFrom).diff(dayjs(currentDate)) <= 0 ? true : false}
          >
            <label>{t('Date from')}</label>
            <DateTimeInput
              name='dateTime'
              placeholder={t('Date from')}
              minDate={!id ? dayjs(new Date()).format('DD.MM.YYYY HH:mm') : ''}
              closable
              markColor={'blue'}
              localization={languageLocal.code}
              closeOnMouseLeave={false}
              initialDate={dayjs(new Date()).format('DD.MM.YYYY HH:mm')}
              dateTimeFormat={'DD.MM.YYYY HH:mm'}
              className={'datePicker'}
              value={watchDateFrom || ''}
              iconPosition='left'
              onChange={(event, { value }) => setValue('from', value)}
            />
            {!watchDateFrom && isSubmitted && <StyledErrorLabel className='ui pointing basic label'>{t('Please enter date from')}</StyledErrorLabel>}
            {dateFrom && currentDate && dayjs(dateFrom).diff(dayjs(currentDate)) <= 0 &&
              <StyledErrorLabel className='ui pointing basic label'>{t('Please select a date ahead of the current date.')}</StyledErrorLabel>}
          </Form.Field>
        }}
      />
      <Controller
        control={control}
        name={'to'}
        rules={{
          required: t('Please enter Date to')
        }}
        render={({ fieldState }) => {
          return <Form.Field
            required
            error={!watchDateTo && isSubmitted ? getError(fieldState) : false}
          >
            <label>{t('Date to')}</label>
            <DateTimeInput
              name='dateTime'
              className={'datePicker'}
              placeholder={t('Date to')}
              closable
              markColor={'blue'}
              initialDate={getMinDate(watchDateFrom)}
              minDate={!id ? getMinDate(watchDateFrom) : ''}
              maxDate={getMaxDate(watchDateFrom)}
              localization={languageLocal.code}
              closeOnMouseLeave={false}
              dateTimeFormat={'DD.MM.YYYY HH:mm'}
              value={watchDateTo || ''}
              iconPosition='left'
              onChange={(event, { value }) => setValue('to', value)}
            />
            {!watchDateTo && isSubmitted && <StyledErrorLabel className='ui pointing basic label'>{t('Please enter date from')}</StyledErrorLabel>}
          </Form.Field>
        }}
      />
    </Form.Group>
    {renderLanguageFields()}
  </>
}

export default DataPopup
